import { useState, useEffect, useCallback, useContext, useRef } from 'react';
import axios from 'axios';
import Loader from '../shared/Loader';
import { API_ENDPOINTS } from '../../config/api';
import { getDisplayImageUrl } from '../../utils/imageUtils';
import { AuthContext } from '../../providers/AuthContext';
import { getAdminAuthHeaders } from '../../utils/adminApi';

const STATUS_CONFIG = {
  'en espera': {
    label: 'En Espera',
    icon: 'fa-clock',
    color: '#f59e0b',
    bg: '#fef3c7',
    darkBg: 'rgba(245, 158, 11, 0.1)',
  },
  'en preparacion': {
    label: 'En Preparación',
    icon: 'fa-fire',
    color: '#f97316',
    bg: '#ffedd5',
    darkBg: 'rgba(249, 115, 22, 0.1)',
  },
  'terminado': {
    label: 'Listo para Retiro',
    icon: 'fa-check-circle',
    color: '#10b981',
    bg: '#d1fae5',
    darkBg: 'rgba(16, 185, 129, 0.1)',
  },
  'enviado': {
    label: 'Enviado',
    icon: 'fa-truck',
    color: '#3b82f6',
    bg: '#dbeafe',
    darkBg: 'rgba(59, 130, 246, 0.1)',
  },
  'entregado': {
    label: 'Entregado',
    icon: 'fa-check-double',
    color: '#059669',
    bg: '#a7f3d0',
    darkBg: 'rgba(5, 150, 105, 0.1)',
  },
  'cancelado': {
    label: 'Cancelado',
    icon: 'fa-ban',
    color: '#ef4444',
    bg: '#fee2e2',
    darkBg: 'rgba(239, 68, 68, 0.1)',
  },
};

const DATE_RANGES = [
  { key: 'today', label: 'Hoy' },
  { key: 'week', label: 'Última Semana' },
  { key: 'month', label: 'Último Mes' },
  { key: 'all', label: 'Todos' },
];

const CUSTOMIZABLE_STATUS_CONFIG = {
  'pending': { label: 'Pendiente', icon: 'fa-clock', color: '#f59e0b', bg: '#fef3c7' },
  'paid': { label: 'Pagado', icon: 'fa-check-circle', color: '#10b981', bg: '#d1fae5' },
  'in-production': { label: 'En Producción', icon: 'fa-cog', color: '#6f42c1', bg: '#ede9fe' },
  'completed': { label: 'Completado', icon: 'fa-check-double', color: '#059669', bg: '#a7f3d0' },
  'cancelled': { label: 'Cancelado', icon: 'fa-ban', color: '#ef4444', bg: '#fee2e2' },
};

const PRODUCT_TYPES_MAP = {
  cuadro: { label: 'Cuadro', icon: 'fa-image' },
  remera: { label: 'Remera', icon: 'fa-shirt' },
  almohadon: { label: 'Almohadón', icon: 'fa-couch' },
  otro: { label: 'Otro', icon: 'fa-box' },
};

const Orders = () => {
  const { token } = useContext(AuthContext);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeStatusTab, setActiveStatusTab] = useState('todos');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState('all');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [expandedOrder, setExpandedOrder] = useState(null);
  const pollingRef = useRef(null);

  // Source toggle: 'tienda' | 'personalizado-ia'
  const [orderSource, setOrderSource] = useState('tienda');

  // Customizable orders state
  const [custOrders, setCustOrders] = useState([]);
  const [custLoading, setCustLoading] = useState(false);
  const [custStats, setCustStats] = useState(null);
  const [custStatusFilter, setCustStatusFilter] = useState('all');
  const [custExpandedOrder, setCustExpandedOrder] = useState(null);
  const [custSearchQuery, setCustSearchQuery] = useState('');

  // Fetch orders
  const fetchOrders = useCallback(async (showLoader = false) => {
    if (showLoader) setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/orders`, {
        headers: getAdminAuthHeaders(token)
      });
      const ordersWithStatus = response.data.map(order => ({
        ...order,
        status: order.status || 'en espera'
      }));
      setOrders(ordersWithStatus);
      setError(null);
    } catch (err) {
      setError('Error al obtener los pedidos');
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Initial load + polling
  useEffect(() => {
    if (orderSource === 'tienda') {
      fetchOrders(true);
      pollingRef.current = setInterval(() => fetchOrders(false), 30000);
      return () => clearInterval(pollingRef.current);
    }
  }, [fetchOrders, orderSource]);

  // Fetch customizable orders
  useEffect(() => {
    if (orderSource === 'personalizado-ia') {
      fetchCustOrders();
      fetchCustStats();
    }
  }, [orderSource, custStatusFilter]);

  const fetchCustOrders = async () => {
    setCustLoading(true);
    try {
      const params = new URLSearchParams({ page: '1', limit: '100' });
      if (custStatusFilter !== 'all') params.append('status', custStatusFilter);
      if (custSearchQuery) params.append('search', custSearchQuery);
      const res = await axios.get(`${API_ENDPOINTS.customizableOrders.getAll()}?${params}`);
      setCustOrders(res.data.orders || []);
    } catch (err) {
      console.error('Error fetching customizable orders:', err);
    } finally {
      setCustLoading(false);
    }
  };

  const fetchCustStats = async () => {
    try {
      const res = await axios.get(API_ENDPOINTS.customizableOrders.stats());
      setCustStats(res.data);
    } catch (err) {
      console.error('Error fetching customizable stats:', err);
    }
  };

  const handleCustStatusChange = async (orderId, newStatus) => {
    try {
      await axios.put(API_ENDPOINTS.customizableOrders.updateStatus(orderId), { status: newStatus });
      fetchCustOrders();
      fetchCustStats();
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  // Status change handler
  const handleStatusChange = async (orderId, newStatus) => {
    setOrders(prev =>
      prev.map(order =>
        order._id === orderId ? { ...order, status: newStatus } : order
      )
    );
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/orders/${orderId}`,
        { status: newStatus },
        { headers: getAdminAuthHeaders(token) }
      );
    } catch (err) {
      // Revert on error
      fetchOrders(false);
    }
  };

  // Date range filter
  const getDateRange = () => {
    const now = new Date();
    if (customDateFrom || customDateTo) {
      return {
        from: customDateFrom ? new Date(customDateFrom + 'T00:00:00') : null,
        to: customDateTo ? new Date(customDateTo + 'T23:59:59') : null,
      };
    }
    switch (dateRange) {
      case 'today': {
        const start = new Date(now);
        start.setHours(0, 0, 0, 0);
        return { from: start, to: now };
      }
      case 'week': {
        const start = new Date(now);
        start.setDate(start.getDate() - 7);
        start.setHours(0, 0, 0, 0);
        return { from: start, to: now };
      }
      case 'month': {
        const start = new Date(now);
        start.setMonth(start.getMonth() - 1);
        start.setHours(0, 0, 0, 0);
        return { from: start, to: now };
      }
      case 'all':
      default:
        return { from: null, to: null };
    }
  };

  // Filtered orders
  const filteredOrders = orders.filter(order => {
    // Status filter
    if (activeStatusTab !== 'todos' && order.status !== activeStatusTab) return false;

    // Date filter
    const range = getDateRange();
    const orderDate = new Date(order.date);
    if (range.from && orderDate < range.from) return false;
    if (range.to && orderDate > range.to) return false;

    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matchNumber = order.orderNumber?.toString().includes(q);
      const matchName = order.buyerInfo?.nombre?.toLowerCase().includes(q);
      const matchEmail = order.buyerInfo?.mail?.toLowerCase().includes(q);
      const matchPhone = order.buyerInfo?.telefono?.includes(q);
      if (!matchNumber && !matchName && !matchEmail && !matchPhone) return false;
    }

    return true;
  });

  // Status counts
  const statusCounts = orders.reduce((acc, order) => {
    acc[order.status] = (acc[order.status] || 0) + 1;
    return acc;
  }, {});

  // KPI calculations (on filtered-by-date orders, not by status)
  const dateFilteredOrders = orders.filter(order => {
    const range = getDateRange();
    const orderDate = new Date(order.date);
    if (range.from && orderDate < range.from) return false;
    if (range.to && orderDate > range.to) return false;
    return true;
  });

  const totalOrders = dateFilteredOrders.length;
  const pendingOrders = dateFilteredOrders.filter(o => o.status === 'en espera').length;
  const preparingOrders = dateFilteredOrders.filter(o => o.status === 'en preparacion').length;
  const totalRevenue = dateFilteredOrders
    .filter(o => o.payment?.status === 'approved')
    .reduce((sum, o) => sum + (o.total || 0), 0);

  // Shipping helpers
  const getShippingIcon = (shippingType) => {
    switch (shippingType) {
      case 'dac_montevideo': return 'fa-motorcycle';
      case 'retiro_local': return 'fa-store';
      case 'envio_interior_dac': return 'fa-box';
      case 'envio_interior_domicilio':
      case 'envio_interior': return 'fa-truck';
      default: return 'fa-box';
    }
  };

  const getShippingLabel = (shipping) => {
    if (!shipping) return null;
    const colorMap = {
      'dac_montevideo': { bg: '#dbeafe', color: '#1e40af' },
      'retiro_local': { bg: '#d1fae5', color: '#065f46' },
      'envio_interior_dac': { bg: '#ede9fe', color: '#5b21b6' },
      'envio_interior_domicilio': { bg: '#fef3c7', color: '#92400e' },
      'envio_interior': { bg: '#fef3c7', color: '#92400e' },
    };
    const colors = colorMap[shipping.type] || { bg: '#f3f4f6', color: '#6b7280' };
    return (
      <span className="orders-shipping-badge" style={{ backgroundColor: colors.bg, color: colors.color }}>
        <i className={`fa-solid ${getShippingIcon(shipping.type)}`}></i>
        {shipping.label}
        {shipping.cost > 0 && ` ($${shipping.cost})`}
      </span>
    );
  };

  const handleDateRangeClick = (rangeKey) => {
    setDateRange(rangeKey);
    setCustomDateFrom('');
    setCustomDateTo('');
  };

  const handleCustomDateChange = (type, value) => {
    if (type === 'from') setCustomDateFrom(value);
    else setCustomDateTo(value);
    setDateRange('custom');
  };

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="orders-error">
        <i className="fa-solid fa-triangle-exclamation"></i>
        <p>{error}</p>
        <button onClick={() => fetchOrders(true)}>Reintentar</button>
      </div>
    );
  }

  // Filtered customizable orders
  const filteredCustOrders = custOrders.filter(order => {
    if (custSearchQuery) {
      const q = custSearchQuery.toLowerCase();
      const matchName = order.customerName?.toLowerCase().includes(q);
      const matchEmail = order.customerEmail?.toLowerCase().includes(q);
      const matchProduct = order.productName?.toLowerCase().includes(q);
      if (!matchName && !matchEmail && !matchProduct) return false;
    }
    return true;
  });

  return (
    <div className="orders-panel">
      {/* Header */}
      <div className="orders-header">
        <div className="orders-header-text">
          <h1>Pedidos Realizados</h1>
          <p>Gestiona y visualiza todos los pedidos de tu tienda</p>
        </div>
        <button className="orders-refresh-btn" onClick={() => orderSource === 'tienda' ? fetchOrders(true) : fetchCustOrders()}>
          <i className="fa-solid fa-arrows-rotate"></i>
          Actualizar
        </button>
      </div>

      {/* Source Toggle */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        <button
          className={`btn ${orderSource === 'tienda' ? 'btn-dark' : 'btn-outline-dark'}`}
          onClick={() => setOrderSource('tienda')}
        >
          <i className="fas fa-shopping-cart" style={{ marginRight: '6px' }}></i>
          Tienda
        </button>
        <button
          className={`btn ${orderSource === 'personalizado-ia' ? 'btn-dark' : 'btn-outline-dark'}`}
          onClick={() => setOrderSource('personalizado-ia')}
        >
          <i className="fas fa-wand-magic-sparkles" style={{ marginRight: '6px' }}></i>
          Personalizado IA
          {custStats && custStats.paid > 0 && (
            <span className="badge bg-danger" style={{ marginLeft: '6px' }}>{custStats.paid}</span>
          )}
        </button>
      </div>

      {/* === TIENDA ORDERS === */}
      {orderSource === 'tienda' && (
      <>
      {/* KPI Cards */}
      <div className="orders-kpi-row">
        <div className="orders-kpi-card">
          <div className="orders-kpi-icon" style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}>
            <i className="fa-solid fa-receipt"></i>
          </div>
          <div className="orders-kpi-info">
            <span className="orders-kpi-value">{totalOrders}</span>
            <span className="orders-kpi-label">Total Pedidos</span>
          </div>
        </div>
        <div className="orders-kpi-card">
          <div className="orders-kpi-icon" style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}>
            <i className="fa-solid fa-clock"></i>
          </div>
          <div className="orders-kpi-info">
            <span className="orders-kpi-value">{pendingOrders}</span>
            <span className="orders-kpi-label">Pendientes</span>
          </div>
        </div>
        <div className="orders-kpi-card">
          <div className="orders-kpi-icon" style={{ background: 'rgba(249, 115, 22, 0.1)', color: '#f97316' }}>
            <i className="fa-solid fa-fire"></i>
          </div>
          <div className="orders-kpi-info">
            <span className="orders-kpi-value">{preparingOrders}</span>
            <span className="orders-kpi-label">En Preparación</span>
          </div>
        </div>
        <div className="orders-kpi-card">
          <div className="orders-kpi-icon" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
            <i className="fa-solid fa-dollar-sign"></i>
          </div>
          <div className="orders-kpi-info">
            <span className="orders-kpi-value">${totalRevenue.toLocaleString()}</span>
            <span className="orders-kpi-label">Ingresos (Pagados)</span>
          </div>
        </div>
      </div>

      {/* Toolbar: Search + Date Filters */}
      <div className="orders-toolbar">
        <div className="orders-toolbar-top">
          <div className="orders-search">
            <i className="fa-solid fa-magnifying-glass"></i>
            <input
              type="text"
              placeholder="Buscar por N° pedido, nombre, email o teléfono..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="orders-search-clear" onClick={() => setSearchQuery('')}>
                <i className="fa-solid fa-xmark"></i>
              </button>
            )}
          </div>
          <div className="orders-date-filters">
            {DATE_RANGES.map(r => (
              <button
                key={r.key}
                className={`orders-date-btn ${dateRange === r.key && !customDateFrom && !customDateTo ? 'active' : ''}`}
                onClick={() => handleDateRangeClick(r.key)}
              >
                {r.label}
              </button>
            ))}
            <div className="orders-date-custom">
              <input
                type="date"
                value={customDateFrom}
                onChange={(e) => handleCustomDateChange('from', e.target.value)}
                title="Desde"
              />
              <span className="orders-date-sep">—</span>
              <input
                type="date"
                value={customDateTo}
                onChange={(e) => handleCustomDateChange('to', e.target.value)}
                title="Hasta"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Status Tabs */}
      <div className="orders-status-tabs">
        <button
          className={`orders-tab ${activeStatusTab === 'todos' ? 'active' : ''}`}
          onClick={() => setActiveStatusTab('todos')}
        >
          <i className="fa-solid fa-layer-group"></i>
          Todos
          <span className="orders-tab-count">{orders.length}</span>
        </button>
        {Object.entries(STATUS_CONFIG).map(([key, config]) => (
          <button
            key={key}
            className={`orders-tab ${activeStatusTab === key ? 'active' : ''}`}
            style={{
              '--tab-color': config.color,
              '--tab-bg': config.bg,
            }}
            onClick={() => setActiveStatusTab(key)}
          >
            <i className={`fa-solid ${config.icon}`}></i>
            {config.label}
            <span className="orders-tab-count" style={
              activeStatusTab === key ? { backgroundColor: config.color, color: '#fff' } : {}
            }>
              {statusCounts[key] || 0}
            </span>
          </button>
        ))}
      </div>

      {/* Orders List - Tienda */}
      <div className="orders-list">
        {filteredOrders.length === 0 ? (
          <div className="orders-empty">
            <i className="fa-solid fa-inbox"></i>
            <h3>No hay pedidos</h3>
            <p>
              {activeStatusTab !== 'todos'
                ? `No hay pedidos con estado "${STATUS_CONFIG[activeStatusTab]?.label || activeStatusTab}"`
                : searchQuery
                  ? 'No se encontraron pedidos con esa búsqueda'
                  : 'No hay pedidos registrados en este período'}
            </p>
          </div>
        ) : (
          <>
            <div className="orders-list-count">
              {filteredOrders.length} pedido{filteredOrders.length !== 1 ? 's' : ''}
            </div>
            {filteredOrders.map(order => {
              const statusConf = STATUS_CONFIG[order.status] || STATUS_CONFIG['en espera'];
              const isExpanded = expandedOrder === order._id;

              return (
                <div
                  key={order._id}
                  className={`orders-card ${isExpanded ? 'expanded' : ''}`}
                  style={{ borderLeftColor: statusConf.color }}
                >
                  {/* Card Header */}
                  <div className="orders-card-header" onClick={() => setExpandedOrder(isExpanded ? null : order._id)}>
                    <div className="orders-card-main">
                      <div className="orders-card-number">
                        <span className="orders-number">#{order.orderNumber}</span>
                        <span
                          className="orders-status-pill"
                          style={{ backgroundColor: statusConf.bg, color: statusConf.color }}
                        >
                          <i className={`fa-solid ${statusConf.icon}`}></i>
                          {statusConf.label}
                        </span>
                        {order.payment?.status === 'approved' && (
                          <span className="orders-paid-badge">
                            <i className="fa-solid fa-check"></i> Pagado
                          </span>
                        )}
                        {order.payment?.status === 'pending' && (
                          <span className="orders-pending-pay-badge">
                            <i className="fa-solid fa-hourglass-half"></i> Pago Pendiente
                          </span>
                        )}
                      </div>
                      <div className="orders-card-meta">
                        <span className="orders-card-date">
                          <i className="fa-regular fa-calendar"></i>
                          {new Date(order.date).toLocaleDateString()}
                        </span>
                        <span className="orders-card-time">
                          <i className="fa-regular fa-clock"></i>
                          {new Date(order.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        {order.buyerInfo && (
                          <span className="orders-card-client">
                            <i className="fa-regular fa-user"></i>
                            {order.buyerInfo.nombre}
                          </span>
                        )}
                        <span className="orders-card-total">
                          <strong>${order.total?.toLocaleString()}</strong>
                        </span>
                      </div>
                    </div>
                    <div className="orders-card-toggle">
                      <i className={`fa-solid fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                    </div>
                  </div>

                  {/* Card Body (expandable) */}
                  {isExpanded && (
                    <div className="orders-card-body">
                      <div className="orders-card-grid">
                        {/* Products */}
                        <div className="orders-card-section">
                          <h4><i className="fa-solid fa-bag-shopping"></i> Productos</h4>
                          <ul className="orders-product-list">
                            {order.items.map((item, idx) => (
                              <li key={idx}>
                                <span className="orders-product-qty">{item.quantity}x</span>
                                <span className="orders-product-name">{item.name}</span>
                                {item.variant && (item.variant.size || item.variant.color) && (
                                  <span className="orders-product-variants">
                                    {item.variant.size && <span className="orders-variant-tag">{item.variant.size}</span>}
                                    {item.variant.color && <span className="orders-variant-tag">{item.variant.color}</span>}
                                  </span>
                                )}
                                <span className="orders-product-price">${item.price}</span>
                              </li>
                            ))}
                          </ul>
                          {order.subtotal && order.subtotal !== order.total && (
                            <div className="orders-totals">
                              <span>Subtotal: ${order.subtotal?.toLocaleString()}</span>
                              <span className="orders-total-final">Total: ${order.total?.toLocaleString()}</span>
                            </div>
                          )}
                        </div>

                        {/* Client */}
                        <div className="orders-card-section">
                          <h4><i className="fa-solid fa-user"></i> Cliente</h4>
                          {order.buyerInfo ? (
                            <div className="orders-client-info">
                              <p><i className="fa-solid fa-user"></i> {order.buyerInfo.nombre}</p>
                              <p><i className="fa-solid fa-envelope"></i> {order.buyerInfo.mail}</p>
                              <p><i className="fa-solid fa-phone"></i> {order.buyerInfo.telefono}</p>
                              {order.buyerInfo.cedula && (
                                <p><i className="fa-solid fa-id-card"></i> {order.buyerInfo.cedula}</p>
                              )}
                            </div>
                          ) : (
                            <p className="orders-no-data">No disponible</p>
                          )}
                        </div>

                        {/* Shipping */}
                        <div className="orders-card-section">
                          <h4><i className="fa-solid fa-truck"></i> Envío</h4>
                          <div className="orders-shipping-info">
                            {getShippingLabel(order.shipping)}
                            {order.shippingAddress && order.shipping?.type !== 'retiro_local' ? (
                              <div className="orders-address">
                                <p>
                                  <i className="fa-solid fa-location-dot"></i>
                                  {order.shippingAddress.direccion}
                                </p>
                                <p>
                                  {order.shippingAddress.ciudad}
                                  {order.shippingAddress.departamento && `, ${order.shippingAddress.departamento}`}
                                </p>
                                {order.shippingAddress.referencias && (
                                  <p className="orders-ref">
                                    <i className="fa-solid fa-circle-info"></i>
                                    {order.shippingAddress.referencias}
                                  </p>
                                )}
                              </div>
                            ) : order.shipping?.type === 'retiro_local' ? (
                              <p className="orders-pickup">
                                <i className="fa-solid fa-store"></i>{' '}
                                {order.shipping.pickupLocation ? (
                                  <span>{order.shipping.pickupLocation.name} · {order.shipping.pickupLocation.city}</span>
                                ) : 'Retiro en tienda'}
                              </p>
                            ) : (
                              <p className="orders-no-data">Sin info de envío</p>
                            )}
                            {order.notes && (
                              <div className="orders-notes">
                                <i className="fa-solid fa-comment"></i>
                                {order.notes}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Status Actions */}
                      <div className="orders-actions">
                        <span className="orders-actions-label">Cambiar estado:</span>
                        <div className="orders-actions-buttons">
                          {Object.entries(STATUS_CONFIG).map(([statusKey, conf]) => {
                            // Skip irrelevant statuses based on shipping type
                            if (order.shipping?.type === 'retiro_local' && (statusKey === 'enviado' || statusKey === 'entregado')) return null;
                            if (order.shipping?.type !== 'retiro_local' && statusKey === 'terminado') return null;

                            const isActive = order.status === statusKey;
                            return (
                              <button
                                key={statusKey}
                                className={`orders-status-btn ${isActive ? 'active' : ''}`}
                                style={{
                                  '--btn-color': conf.color,
                                  '--btn-bg': conf.bg,
                                  backgroundColor: isActive ? conf.color : 'transparent',
                                  color: isActive ? '#fff' : conf.color,
                                  borderColor: conf.color,
                                }}
                                onClick={() => handleStatusChange(order._id, statusKey)}
                              >
                                <i className={`fa-solid ${conf.icon}`}></i>
                                {conf.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>
      </>
      )}

      {/* === PERSONALIZADO IA ORDERS === */}
      {orderSource === 'personalizado-ia' && (
        <div>
          {/* Stats */}
          {custStats && (
            <div className="orders-kpi-row" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
              <div className="orders-kpi-card">
                <div className="orders-kpi-icon" style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}>
                  <i className="fa-solid fa-receipt"></i>
                </div>
                <div className="orders-kpi-info">
                  <span className="orders-kpi-value">{custStats.total}</span>
                  <span className="orders-kpi-label">Total</span>
                </div>
              </div>
              <div className="orders-kpi-card">
                <div className="orders-kpi-icon" style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}>
                  <i className="fa-solid fa-clock"></i>
                </div>
                <div className="orders-kpi-info">
                  <span className="orders-kpi-value">{custStats.pending}</span>
                  <span className="orders-kpi-label">Pendientes</span>
                </div>
              </div>
              <div className="orders-kpi-card">
                <div className="orders-kpi-icon" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
                  <i className="fa-solid fa-check-circle"></i>
                </div>
                <div className="orders-kpi-info">
                  <span className="orders-kpi-value">{custStats.paid}</span>
                  <span className="orders-kpi-label">Pagados</span>
                </div>
              </div>
              <div className="orders-kpi-card">
                <div className="orders-kpi-icon" style={{ background: 'rgba(111, 66, 193, 0.1)', color: '#6f42c1' }}>
                  <i className="fa-solid fa-cog"></i>
                </div>
                <div className="orders-kpi-info">
                  <span className="orders-kpi-value">{custStats.inProduction}</span>
                  <span className="orders-kpi-label">En Producción</span>
                </div>
              </div>
              <div className="orders-kpi-card">
                <div className="orders-kpi-icon" style={{ background: 'rgba(5, 150, 105, 0.1)', color: '#059669' }}>
                  <i className="fa-solid fa-check-double"></i>
                </div>
                <div className="orders-kpi-info">
                  <span className="orders-kpi-value">{custStats.completed}</span>
                  <span className="orders-kpi-label">Completados</span>
                </div>
              </div>
            </div>
          )}

          {/* Search + Status Filter */}
          <div className="orders-toolbar">
            <div className="orders-toolbar-top">
              <div className="orders-search">
                <i className="fa-solid fa-magnifying-glass"></i>
                <input
                  type="text"
                  placeholder="Buscar por nombre, email o producto..."
                  value={custSearchQuery}
                  onChange={(e) => setCustSearchQuery(e.target.value)}
                />
                {custSearchQuery && (
                  <button className="orders-search-clear" onClick={() => setCustSearchQuery('')}>
                    <i className="fa-solid fa-xmark"></i>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Status Tabs */}
          <div className="orders-status-tabs">
            <button
              className={`orders-tab ${custStatusFilter === 'all' ? 'active' : ''}`}
              onClick={() => setCustStatusFilter('all')}
            >
              <i className="fa-solid fa-layer-group"></i>
              Todos
              <span className="orders-tab-count">{custStats?.total || 0}</span>
            </button>
            {Object.entries(CUSTOMIZABLE_STATUS_CONFIG).map(([key, config]) => (
              <button
                key={key}
                className={`orders-tab ${custStatusFilter === key ? 'active' : ''}`}
                style={{ '--tab-color': config.color, '--tab-bg': config.bg }}
                onClick={() => setCustStatusFilter(key)}
              >
                <i className={`fa-solid ${config.icon}`}></i>
                {config.label}
                <span className="orders-tab-count" style={
                  custStatusFilter === key ? { backgroundColor: config.color, color: '#fff' } : {}
                }>
                  {custStats?.[key === 'in-production' ? 'inProduction' : key] || 0}
                </span>
              </button>
            ))}
          </div>

          {/* Orders List */}
          {custLoading ? <Loader /> : filteredCustOrders.length === 0 ? (
            <div className="orders-empty">
              <i className="fa-solid fa-inbox"></i>
              <h3>No hay pedidos personalizados</h3>
              <p>No se encontraron pedidos con este filtro</p>
            </div>
          ) : (
            <>
              <div className="orders-list-count">
                {filteredCustOrders.length} pedido{filteredCustOrders.length !== 1 ? 's' : ''} personalizado(s)
              </div>
              <div className="orders-list">
                {filteredCustOrders.map(order => {
                  const statusConf = CUSTOMIZABLE_STATUS_CONFIG[order.status] || CUSTOMIZABLE_STATUS_CONFIG['pending'];
                  const typeInfo = PRODUCT_TYPES_MAP[order.productType] || PRODUCT_TYPES_MAP.otro;
                  const isExpanded = custExpandedOrder === order._id;

                  return (
                    <div
                      key={order._id}
                      className={`orders-card ${isExpanded ? 'expanded' : ''}`}
                      style={{ borderLeftColor: statusConf.color }}
                    >
                      {/* Card Header */}
                      <div className="orders-card-header" onClick={() => setCustExpandedOrder(isExpanded ? null : order._id)}>
                        <div className="orders-card-main">
                          <div className="orders-card-number">
                            <span className="orders-number">
                              <i className={`fas ${typeInfo.icon}`} style={{ marginRight: '6px' }}></i>
                              {order.productName}
                            </span>
                            <span
                              className="orders-status-pill"
                              style={{ backgroundColor: statusConf.bg, color: statusConf.color }}
                            >
                              <i className={`fa-solid ${statusConf.icon}`}></i>
                              {statusConf.label}
                            </span>
                            {order.payment?.status === 'approved' && (
                              <span className="orders-paid-badge">
                                <i className="fa-solid fa-check"></i> Pagado
                              </span>
                            )}
                          </div>
                          <div className="orders-card-meta">
                            <span className="orders-card-date">
                              <i className="fa-regular fa-calendar"></i>
                              {new Date(order.createdAt).toLocaleDateString()}
                            </span>
                            <span className="orders-card-client">
                              <i className="fa-regular fa-user"></i>
                              {order.customerName}
                            </span>
                            <span className="orders-card-total">
                              <strong>${order.totalPrice?.toLocaleString()}</strong>
                            </span>
                          </div>
                        </div>
                        <div className="orders-card-toggle">
                          <i className={`fa-solid fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                        </div>
                      </div>

                      {/* Card Body */}
                      {isExpanded && (
                        <div className="orders-card-body">
                          <div className="orders-card-grid">
                            {/* Producto */}
                            <div className="orders-card-section">
                              <h4><i className={`fas ${typeInfo.icon}`}></i> Producto</h4>
                              <div className="orders-client-info">
                                <p><strong>Tipo:</strong> {typeInfo.label}</p>
                                <p><strong>Nombre:</strong> {order.productName}</p>
                                {order.selectedSize && <p><strong>Tamaño:</strong> {order.selectedSize}</p>}
                                <p><strong>Cantidad:</strong> {order.quantity}</p>
                                <p><strong>Precio unitario:</strong> ${order.unitPrice?.toLocaleString()}</p>
                                <p><strong>Total:</strong> ${order.totalPrice?.toLocaleString()}</p>
                              </div>
                            </div>

                            {/* Cliente */}
                            <div className="orders-card-section">
                              <h4><i className="fa-solid fa-user"></i> Cliente</h4>
                              <div className="orders-client-info">
                                <p><i className="fa-solid fa-user"></i> {order.customerName}</p>
                                <p><i className="fa-solid fa-envelope"></i> {order.customerEmail}</p>
                                {order.customerPhone && <p><i className="fa-solid fa-phone"></i> {order.customerPhone}</p>}
                              </div>
                            </div>

                            {/* Datos personalizados */}
                            {order.customFieldValues && Object.keys(order.customFieldValues).length > 0 && (
                              <div className="orders-card-section">
                                <h4><i className="fa-solid fa-pen-fancy"></i> Datos del Producto</h4>
                                <div className="orders-client-info">
                                  {Object.entries(order.customFieldValues).map(([key, val]) => (
                                    <p key={key}><strong>{key}:</strong> {val}</p>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Imagen del cliente */}
                            <div className="orders-card-section">
                              <h4><i className="fa-solid fa-image"></i> Imagen del Cliente</h4>
                              {order.customImage?.url ? (
                                <div>
                                  <img
                                    src={getDisplayImageUrl(order.customImage.url)}
                                    alt="Personalización"
                                    style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px', border: '1px solid #dee2e6' }}
                                  />
                                  <p style={{ fontSize: '0.8rem', color: '#888', marginTop: '6px' }}>
                                    {order.customImage.originalName}
                                  </p>
                                </div>
                              ) : (
                                <p className="orders-no-data">Sin imagen</p>
                              )}
                            </div>
                          </div>

                          {/* Instrucciones */}
                          {order.instructions && (
                            <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '12px', margin: '12px 0' }}>
                              <strong><i className="fas fa-comment" style={{ marginRight: '6px' }}></i>Instrucciones:</strong>
                              <p style={{ margin: '6px 0 0' }}>{order.instructions}</p>
                            </div>
                          )}

                          {/* Status Actions */}
                          <div className="orders-actions">
                            <span className="orders-actions-label">Cambiar estado:</span>
                            <div className="orders-actions-buttons">
                              {Object.entries(CUSTOMIZABLE_STATUS_CONFIG).map(([statusKey, conf]) => {
                                const isActive = order.status === statusKey;
                                return (
                                  <button
                                    key={statusKey}
                                    className={`orders-status-btn ${isActive ? 'active' : ''}`}
                                    style={{
                                      '--btn-color': conf.color,
                                      '--btn-bg': conf.bg,
                                      backgroundColor: isActive ? conf.color : 'transparent',
                                      color: isActive ? '#fff' : conf.color,
                                      borderColor: conf.color,
                                    }}
                                    onClick={() => handleCustStatusChange(order._id, statusKey)}
                                  >
                                    <i className={`fa-solid ${conf.icon}`}></i>
                                    {conf.label}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Orders;